import { AnyRoute } from "@tanstack/react-router";
import { ZodTypeAny } from "zod";

import { DarwinPage } from "@/types/chatbot.types";
import { getSearchSchema } from "@/utils/search-validator";

type ZodDef = {
    typeName?: string;
    innerType?: ZodTypeAny;
    schema?: ZodTypeAny;
    out?: ZodTypeAny;
    type?: ZodTypeAny;
    values?: readonly string[] | Record<string, string | number>;
    value?: unknown;
    options?: ZodTypeAny[];
};

// Wrappers that don't change which values a param accepts
const PASSTHROUGH_ZOD_TYPES: Record<string, keyof ZodDef> = {
    ZodOptional: "innerType",
    ZodNullable: "innerType",
    ZodDefault: "innerType",
    ZodCatch: "innerType",
    ZodReadonly: "innerType",
    ZodBranded: "type",
    ZodEffects: "schema",
    ZodPipeline: "out",
};

/** Short, model-readable description of the values a search param accepts. */
export function describeZodType(type: ZodTypeAny): string {
    const def = type._def as ZodDef;
    const typeName = def.typeName ?? "";

    const inner = PASSTHROUGH_ZOD_TYPES[typeName];
    if (inner) return describeZodType(def[inner] as ZodTypeAny);

    switch (typeName) {
        case "ZodEnum":
            return (def.values as readonly string[]).join("|");
        case "ZodNativeEnum":
            return Object.values(def.values as Record<string, string | number>)
                .filter((value) => typeof value === "string")
                .join("|");
        case "ZodLiteral":
            return JSON.stringify(def.value);
        case "ZodArray":
            return `${describeZodType(def.type as ZodTypeAny)}[]`;
        case "ZodUnion":
            return (def.options ?? []).map(describeZodType).join("|");
        case "ZodString":
            return "string";
        case "ZodNumber":
            return "number";
        case "ZodBoolean":
            return "boolean";
        case "ZodDate":
            return "date";
        default:
            return "value";
    }
}

/** Same rule as the root route guard: the deepest route in the branch that declares permissions wins. */
function getRoutePermissions(route: AnyRoute): string[] {
    for (let current: AnyRoute | undefined = route; current; current = current.parentRoute) {
        const permissions = current.options?.staticData?.permissions;
        if (Array.isArray(permissions) && permissions.length > 0) return permissions;
    }
    return [];
}

function describeSearchParams(route: AnyRoute): string | undefined {
    const schema = getSearchSchema(route.options?.validateSearch);
    if (!schema) return undefined;

    const params = Object.entries(schema.shape as Record<string, ZodTypeAny>).map(
        ([key, type]) => `${key}: ${describeZodType(type)}`
    );
    return params.length > 0 ? params.join("; ") : undefined;
}

/**
 * Elitphone pages the user can open, with their path params (`$name` segments) and allowed
 * query params, so Darwin can link to them.
 */
export function buildDarwinPageCatalog({
    routes,
    permissionCodes,
    translate,
}: {
    routes: AnyRoute[];
    permissionCodes: string[];
    translate: (key: string) => string;
}): DarwinPage[] {
    return routes
        .filter((route) => Boolean(route.fullPath) && !route.options?.staticData?.hiddenFromDarwin)
        .filter((route) => {
            const permissions = getRoutePermissions(route);
            return permissions.length === 0 || permissions.some((code) => permissionCodes.includes(code));
        })
        .map((route) => {
            const config = route.options?.staticData?.config;
            const titleKey = config?.title ?? config?.breadcrumb?.title;
            return {
                path: route.fullPath.replace(/\/$/, "") || "/",
                title: titleKey ? translate(titleKey) : undefined,
                query: describeSearchParams(route),
            };
        })
        .sort((a, b) => a.path.localeCompare(b.path));
}
